/**
 * cconfig format migration utilities.
 *
 * The compact format (v2) uses short field names and drops redundant data
 * to reduce cconfig payload size by ~65%.  Read helpers normalise either
 * format into the original "expanded" shape so downstream code is unchanged.
 * Write helpers always produce the compact format.
 *
 * Format detection:
 *   pos_mapping  — compact has `m` key, legacy has `mappings`
 *   servingSizes — compact has `s` array, legacy has `sizes` object
 */

// ── pos_mapping ──────────────────────────────────────────────────────────────

/**
 * Normalise a parsed pos_mapping object to the expanded (legacy) shape.
 * Accepts either legacy or compact format.
 *
 * @param {Object} raw  Parsed pos_mapping value from cconfig
 * @returns {Object|null}  Expanded pos_mapping object, or null if input is falsy
 */
export function normalizePosMapping(raw) {
  if (!raw) return null;

  // Compact format: has `m` key
  if (raw.m !== undefined) {
    const expandVariations = vs => (vs || []).map(v => ({
      square_variation_id: v.vid,
      variation_name: v.n || null,
      price_cents: v.p,
      enabled: true,
      size_ml: v.s,
    }));

    return {
      provider: raw.prov,
      location_id: raw.loc,
      updated_at: raw.ts,
      mappings: (raw.m || []).map(m => ({
        device_id: m.did,
        port_num: m.port,
        square_item_id: m.iid,
        square_item_name: null,
        tap_label: null,
        keg_name: null,
        // kn: the keg that was on this tap when the mapping was last saved.
        // Comparing it to the tap's current keg name is how the POS Variance
        // report detects a mapping left behind by a keg change — exact, and
        // independent of how the venue names its POS items. Absent on mappings
        // saved before this field existed; consumers fall back to timestamps.
        keg_name_at_save: m.kn || null,
        variations: expandVariations(m.v),
        // prev: snapshot of the mapping that was active before the most recent
        // item swap. Used by the POS Variance report to show sold data for the
        // previous keg run without requiring a full mapping history.
        prev: m.prev ? {
          square_item_id: m.prev.iid || null,
          variations: expandVariations(m.prev.v),
          replaced_at: m.prev.rat || null,
          // pkn: the keg the prev mapping was saved for. Lets the report pick a
          // run's mapping era by keg NAME instead of the save timestamp, which
          // lags the actual keg change whenever the operator relinks late.
          keg_name_at_save: m.prev.pkn || null,
          // rs: what dated replaced_at — 'sale' (the first POS sale of the newly
          // linked item, i.e. real evidence of when the keg changed) or 'save'
          // (the moment the operator hit Save, which may lag by days). Absent on
          // legacy snapshots; consumers treat null as 'save'.
          replaced_source: m.prev.rs || null,
        } : null,
      })),
    };
  }

  // Unknown format — treat as no mapping (no legacy data exists for pos_mapping)
  return null;
}

/**
 * Convert an expanded pos_mapping object to compact format for writing.
 * Drops display-only fields (tap_label, keg_name, square_item_name,
 * variation_name) and disabled variations.
 *
 * @param {Object} data  Expanded pos_mapping object
 * @returns {Object}     Compact pos_mapping for cconfig storage
 */
export function compactPosMapping(data) {
  const compactVariations = (vs, { includeNames = true } = {}) => (vs || [])
    .filter(v => v.enabled !== false)
    .map(v => ({
      vid: v.square_variation_id,
      ...(includeNames && v.variation_name ? { n: v.variation_name } : {}),
      p: v.price_cents,
      s: v.size_ml,
    }));

  return {
    prov: data.provider,
    loc: data.location_id,
    ts: data.updated_at,
    m: (data.mappings || []).map(m => ({
      did: m.device_id,
      port: m.port_num,
      iid: m.square_item_id,
      v: compactVariations(m.variations),
      // Record the keg on the tap at save time so a later keg change is
      // detectable. Omitted when unknown so we never write an empty marker.
      ...(m.keg_name_at_save ? { kn: m.keg_name_at_save } : {}),
      // Persist prev snapshot if present so the POS Variance report can look
      // up sold data for the immediately prior keg run on this tap.
      //
      // Variation names are deliberately NOT stored in prev. Lightspeed names
      // are long ("KH LAGER PINTE — Happy Hour") and a busy site stores them
      // twice (live + prev), which filled a 28-tap customer's 32 KB cconfig and
      // blocked saving. The report resolves names from the POS catalog and
      // sales instead, falling back to the tap's serving-size label. Stripping
      // on every write also shrinks snapshots saved before this change.
      ...(m.prev ? {
        prev: {
          ...(m.prev.square_item_id ? { iid: m.prev.square_item_id } : {}),
          v: compactVariations(m.prev.variations, { includeNames: false }),
          ...(m.prev.replaced_at ? { rat: m.prev.replaced_at } : {}),
          // Keg the outgoing mapping was for — the anchor for name-based era
          // selection. Omitted when unknown so we never write an empty marker.
          ...(m.prev.keg_name_at_save ? { pkn: m.prev.keg_name_at_save } : {}),
          // How replaced_at was dated ('sale' | 'save'). Omitted when unknown so
          // legacy snapshots stay byte-identical and read back as 'save'.
          ...(m.prev.replaced_source ? { rs: m.prev.replaced_source } : {}),
        },
      } : {}),
    })),
  };
}

// ── enabledServingSizes ──────────────────────────────────────────────────────

/**
 * Normalise a parsed enabledServingSizes object to the expanded (legacy) shape.
 * Accepts either legacy or compact format.
 *
 * @param {Object} raw  enabledServingSizes value from cconfig
 * @returns {Object|null}  Expanded enabledServingSizes, or null if input is falsy
 */
export function normalizeServingSizes(raw) {
  if (!raw || typeof raw !== 'object') return null;

  // Compact format: `s` is an array of enabled size keys
  if (Array.isArray(raw.s)) {
    const sizes = {};
    raw.s.forEach(k => { sizes[k] = true; });

    const customSizes = (raw.cs || []).map(c => ({ size: c.s, name: c.n }));

    const tapSizes = {};
    if (raw.ts) {
      Object.entries(raw.ts).forEach(([key, val]) => {
        const tapEntry = { sizes: {}, customSizes: [] };
        if (Array.isArray(val.s)) {
          val.s.forEach(k => { tapEntry.sizes[k] = true; });
        }
        if (val.cs) {
          tapEntry.customSizes = val.cs.map(c => ({ size: c.s, name: c.n }));
        }
        tapSizes[key] = tapEntry;
      });
    }

    return {
      sizes,
      customSizes,
      tapSizes,
      tolerance: raw.tol,
      splitPourThreshold: raw.spt,
    };
  }

  // Legacy format — return as-is
  return raw;
}

/**
 * Convert an expanded enabledServingSizes object to compact format for writing.
 *
 * @param {Object} data  Expanded enabledServingSizes
 * @returns {Object}     Compact enabledServingSizes for cconfig storage
 */
export function compactServingSizes(data) {
  const result = {};

  // Convert sizes boolean map → array of enabled keys
  if (data.sizes) {
    result.s = Object.entries(data.sizes)
      .filter(([, enabled]) => enabled)
      .map(([key]) => parseInt(key, 10));
  }

  // Convert customSizes
  if (data.customSizes && data.customSizes.length > 0) {
    result.cs = data.customSizes.map(c => ({ s: c.size, n: c.name }));
  }

  // Convert tapSizes
  if (data.tapSizes && Object.keys(data.tapSizes).length > 0) {
    result.ts = {};
    Object.entries(data.tapSizes).forEach(([key, val]) => {
      const entry = {};
      if (val.sizes) {
        entry.s = Object.entries(val.sizes)
          .filter(([, enabled]) => enabled)
          .map(([k]) => parseInt(k, 10));
      }
      if (val.customSizes && val.customSizes.length > 0) {
        entry.cs = val.customSizes.map(c => ({ s: c.size, n: c.name }));
      }
      result.ts[key] = entry;
    });
  }

  if (data.tolerance !== undefined) result.tol = data.tolerance;
  if (data.splitPourThreshold !== undefined) result.spt = data.splitPourThreshold;

  return result;
}
